/**
 * WorkspaceStatsHero — Phase B4 personalized stats hero for the populated
 * "Your Work" tracker. Presentational: all three stats are computed from the
 * artifact list the tracker already fetched (no extra request). Rendered only
 * when there is >= 1 artifact (the empty state has its own onboarding hero).
 */
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PortfolioArtifact } from "../../api/client";

interface WorkspaceStatsHeroProps {
  artifacts: PortfolioArtifact[];
  displayName: string | null;
}

/** Mean of overall_score across artifacts that have a numeric one, rounded to
 *  1 decimal; null when none are scored. Narrows the loosely-typed rubric_score
 *  JSONB defensively (mirrors ArtifactRubric's readScoreGrid). */
function averageScore(artifacts: PortfolioArtifact[]): number | null {
  const scores: number[] = [];
  for (const a of artifacts) {
    const raw = a.rubric_score as Record<string, unknown> | null;
    const overall =
      raw && typeof raw === "object" ? (raw as { overall_score?: unknown }).overall_score : undefined;
    if (typeof overall === "number") scores.push(overall);
  }
  if (scores.length === 0) return null;
  return Math.round((scores.reduce((s, n) => s + n, 0) / scores.length) * 10) / 10;
}

function StatTile({ value, label }: { value: string; label: string }) {
  return (
    <Paper variant="outlined" sx={{ width: "100%", p: 2, borderRadius: 2, textAlign: "center" }}>
      <Typography variant="h4" sx={{ fontWeight: 500, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Paper>
  );
}

export default function WorkspaceStatsHero({
  artifacts,
  displayName,
}: WorkspaceStatsHeroProps) {
  const { t } = useTranslation("portfolio-kit");
  const inProgress = artifacts.filter((a) => a.status === "in_progress").length;
  const complete = artifacts.filter((a) => a.status === "complete").length;
  const avg = averageScore(artifacts);
  const firstName = displayName?.trim().split(/\s+/)[0] || "";

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 500, mb: 2 }}>
        {firstName
          ? t("WorkspaceStatsHero.welcomeBackNamed", { name: firstName })
          : t("WorkspaceStatsHero.welcomeBack")}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <StatTile
            value={String(inProgress)}
            label={t("WorkspaceStatsHero.inProgressLabel")}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatTile
            value={String(complete)}
            label={t("WorkspaceStatsHero.completeLabel")}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <StatTile
            value={avg !== null ? avg.toFixed(1) : "—"}
            label={t("WorkspaceStatsHero.avgScoreLabel")}
          />
          {avg === null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", mt: 0.5 }}
            >
              {t("WorkspaceStatsHero.scoreArtifactPrompt")}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
